import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import './home.css';
import AccessDeniedModal from '../common/AccessDeniedModal';
import Lightbox from '../common/Lightbox';
import { formatDateVN } from '../../utils/formatDate';

export default function PostDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const apiUrl = (process.env.REACT_APP_API_URL);
  const [post, setPost] = useState(null);
  const [lightboxItems, setLightboxItems] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      setLoading(true); setError(null);
      try {
        if (!apiUrl) { setError('API not configured'); return; }
        const token = localStorage.getItem('token');
        if (!token) { setError('Không được xác thực'); return; }
        const res = await fetch(`${apiUrl}/api/posts`, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error('Failed to fetch posts');
        const data = await res.json();
        if (!mounted) return;
        const found = (data.posts || []).find(p => String(p.id) === String(id));
        if (found) setPost(found);
        else setError('Không tìm thấy bài viết');
      } catch (e) { console.error(e); setError('Lỗi khi tải bài viết'); }
      finally { setLoading(false); }
    }
    load();
    return () => { mounted = false; };
  }, [id]);

  if (loading) return <div className="home-feed">Loading…</div>;
  const isAccessDeniedError = (err) => {
    if (!err) return false;
    const s = String(err).toLowerCase();
    return s.includes('không được xác thực') || s.includes('không có quyền') || s.includes('permission') || s.includes('403');
  };

  if (error) {
    if (isAccessDeniedError(error)) return <AccessDeniedModal show={true} message={error} />;
    return <div className="home-feed"><div className="error">{error}</div><button onClick={() => navigate(-1)}>Quay lại</button></div>;
  }
  if (!post) return <div className="home-feed">No post</div>;

  return (
    <div className="home-feed">
      {/* <button className="back" onClick={() => navigate(-1)}>← Back</button> */}
      <article className="post detail">
        <header>
          <h2>{post.title}</h2>
          <div className="meta">{(post.author_last_name || post.author_first_name) ? [post.author_last_name, post.author_first_name].filter(Boolean).join(' ') : post.author} — {formatDateVN(post.createdAt)}</div>
        </header>
        <div className="content">{post.content}</div>
        {post.attachments && post.attachments.length > 0 && (
          <div className="attachment-grid">
            {post.attachments.map((a, idx) => {
              const src = (a && (a.url || a.path)) || '';
              let resolved = src;
              if (src && src.startsWith('/')) {
                const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
                resolved = base + src;
              } else if (src && !src.match(/^https?:\/\//)) {
                const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
                resolved = base + '/' + src.replace(/^\//, '');
              }
              const isImage = a.mime && a.mime.startsWith('image/');
              const isVideo = a.mime && a.mime.startsWith('video/');
              return (
                <div key={idx} className="att">
                  {isImage && (
                    <img
                      src={resolved}
                      alt={a.filename || ''}
                      onClick={(ev) => {
                        ev.stopPropagation();
                        // build lightbox items with resolved urls
                        const items = (post.attachments || []).map((aa) => {
                          const s = (aa && (aa.url || aa.path)) || '';
                          let r = s;
                          if (s && s.startsWith('/')) {
                            const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
                            r = base + s;
                          } else if (s && !s.match(/^https?:\/\//)) {
                            const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
                            r = base + '/' + s.replace(/^\//, '');
                          }
                          return { ...aa, url: r };
                        });
                        setLightboxItems(items);
                        setLightboxIndex(idx);
                        setLightboxOpen(true);
                      }}
                    />
                  )}
                  {isVideo && <video src={resolved} controls />}
                  {(!a.mime || (!isImage && !isVideo)) && <a href={resolved} target="_blank" rel="noreferrer">{a.filename || 'Tệp'}</a>}
                </div>
              );
            })}
          </div>
        )}
        {lightboxOpen && <Lightbox items={lightboxItems} startIndex={lightboxIndex} onClose={() => setLightboxOpen(false)} />}
        <div className="comments full" onClick={(ev) => { ev.stopPropagation(); }}>
          <h4>Comments ({(post.comments || []).length})</h4>
          {(post.comments || []).slice().reverse().map((c, i) => {
            const last = c.last_name || c.lastName || c.lastname || c.family_name || '';
            const first = c.first_name || c.firstName || c.firstname || c.given_name || '';
            // Strictly use last_name + first_name from users table. If missing, fall back to username only.
            let commenterName = '';
            if (last || first) commenterName = [last, first].filter(Boolean).join(' ');
            else commenterName = (typeof c.user === 'string') ? c.user : (c.user && c.user.username) ? c.user.username : '';

            const maybeAvt = c.avt || c.user_avt || c.avatar || null;
            let avatar = maybeAvt || '';
            if (avatar && avatar.startsWith('/')) {
              const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
              avatar = base + avatar;
            } else if (avatar && !avatar.match(/^https?:\/\//)) {
              const base = apiUrl ? apiUrl.replace(/\/$/, '') : '';
              avatar = base + '/' + avatar.replace(/^\//, '');
            }
            if (!avatar) avatar = (apiUrl ? apiUrl.replace(/\/$/, '') + '/uploads/avt/avt-def.jpg' : '/uploads/avt/avt-def.jpg');

            return (
              <div key={i} className="comment comment-row" onClick={(ev) => { ev.stopPropagation(); }}>
                <img className="comment-avt" src={avatar} alt={commenterName} width={32} height={32} />
                <div className="comment-body">
                  <div className="comment-header">
                    <b>{commenterName || c.user || 'User'}</b>
                    {(c && (c.createdAt || c.created_at)) ? <span className="comment-time">{formatDateVN(c.createdAt || c.created_at)}</span> : null}
                  </div>
                  <div>{c.text}</div>
                </div>
              </div>
            );
          })}
          <div className="comment-box" onClick={(ev) => { ev.stopPropagation(); }}>
            <input placeholder="Write a comment..." value={commentText} onChange={(e) => setCommentText(e.target.value)} />
            <button onClick={async () => {
              const text = (commentText || '').trim();
              if (!text) return;
              // optimistic update: include user's name/avatar so UI shows display name immediately
              const nowIso = (new Date()).toISOString();
              const newComment = {
                user: user && user.username ? user.username : (localStorage.getItem('username') || ''),
                text,
                first_name: user && (user.first_name || user.firstName) ? (user.first_name || user.firstName) : undefined,
                last_name: user && (user.last_name || user.lastName) ? (user.last_name || user.lastName) : undefined,
                avt: user && (user.avt || user.avatar) ? (user.avt || user.avatar) : undefined,
                createdAt: nowIso,
                created_at: nowIso
              };
              setPost(p => ({ ...p, comments: [...(p.comments || []), newComment] }));
              setCommentText('');
              try {
                const token = localStorage.getItem('token');
                if (!apiUrl || !token) throw new Error('No api/token');
                const res = await fetch(`${apiUrl}/api/posts/${id}/comment`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ text }) });
                if (!res.ok) throw new Error('comment failed');
                const data = await res.json();
                // replace last optimistic comment with server one if provided
                if (data && data.comment) {
              const serverComment = { user: data.comment.user, text: data.comment.text, first_name: data.comment.first_name, last_name: data.comment.last_name, avt: data.comment.avt };
              if (user && serverComment.user && String(serverComment.user) === String(user.username)) {
                serverComment.first_name = serverComment.first_name || (user.first_name || user.firstName) || undefined;
                serverComment.last_name = serverComment.last_name || (user.last_name || user.lastName) || undefined;
                serverComment.avt = serverComment.avt || (user.avt || user.avatar) || undefined;
              }
              setPost(p => ({ ...p, comments: [...(p.comments || []).slice(0, -1), serverComment] }));
                }
              } catch (e) {
                console.error('Post comment failed', e);
              }
            }}>Comment</button>
          </div>
        </div>
      </article>
    </div>
  );
}
